import React, { useContext, useEffect, useState } from "react";

import { Preview } from "@creatomate/preview";
import { makeDelete, makePost } from "@culturehq/client";
import { Button, Icon, Tooltip } from "@culturehq/components";

import { ElementSource } from "chq";
import { useAWSUpload, useSignUpload } from "app/hooks";
import { Trans, errorService, t } from "app/utils";

import useAddTags from "components/stories/post-stories-v2/hooks/useAddTags";
import { CreatomateContext } from "../../contexts/VideoStudioContext";
import { updateSubtitleIndexesInCreatomate, updateSubtitlesText } from "../../sidebar/tabs/subtitle/utils";
import { getCreatomateMediaData } from "../../utils/creatomate/utils";
import { VIDEO_TYPE } from "../../utils/constants";
import { StoryModalContext } from "../../contexts/StoryContext";

interface SaveButtonProps {
  onClose: () => void;
  preview: Preview;
}

const SaveBtn: React.FC<SaveButtonProps> = ({ onClose, preview }) => {
  const {
    data,
    handleUpdateData,
    handleAutosaveDraft,
    restartData
  } = useContext(CreatomateContext);
  const { selectedBody, selectedChannel, selectedOptions } = useContext(StoryModalContext);
  const { ready } = preview;
  const {
    burnSubtitles,
    loading,
    mediaType,
    montage,
    onMediaEdited,
    onPosted,
    onThirdPartyMediasReload,
    pendingSubtitle,
    selectedMediaVersion,
    selectedThumbnail,
    story,
    storyQuestionId,
    subtitles,
    subtitlesOn,
    uploadingMedia
  } = data;
  const elements = preview.state?.elements || [];

  const [btnLabel, setBtnLabel] = useState("Save Changes");
  const signUpload = useSignUpload();
  const { waitForS3 } = useAWSUpload();

  useEffect(
    () => {
      if (story?.id) {
        setBtnLabel(mediaType === VIDEO_TYPE ? "Update Video" : "Update Image");
      }
    }, [story, mediaType]
  );

  const addTags = useAddTags({ tags: selectedOptions });

  const callProcessMedia = async (body: Record<string, any>) => makePost("/stories/process_media", body);

  const deleteSubtitles = async () => {
    const toDeleteSubtitles = story?.media?.subtitles?.map(
      subtitle => ({ ...subtitle, deleted: true }));
    if (!toDeleteSubtitles?.length) return;

    await handleAutosaveDraft({
      subtitles: toDeleteSubtitles,
      burnSubtitles,
      draft: false
    });
  };

  const postEdition = async (mediaData: ElementSource) => {
    let body = {
      storyId: story?.id,
      mediaData,
      burnSubtitles: burnSubtitles && subtitlesOn,
      removeSubtitles: selectedMediaVersion?.original
    } as Record<string, any>;

    if (selectedThumbnail) {
      const newThumbnailUrl = selectedThumbnail instanceof Blob
        ? await signUpload(selectedThumbnail) : selectedThumbnail;
      body = { ...body, thumbnail: newThumbnailUrl };
      await waitForS3(2000, body, callProcessMedia);
    } else {
      await callProcessMedia(body);
    }
  };

  const saveMontage = async (): Promise<void> => {
    const params: Record<string, any> = {
      storyQuestionId,
      body: addTags(selectedBody),
      slackChannel: selectedChannel,
      mediaData: getCreatomateMediaData(preview, burnSubtitles)
    };
    await makePost("/stories/generate_montage", params);

    restartData();
    preview.dispose();
    onClose();
  };

  const handleSaveEdition = async (): Promise<void> => {
    if (montage) {
      await saveMontage();
      return;
    }

    if (!ready || !story) return;

    try {
      handleUpdateData({ blockSubtitles: false, loading: true, originalReplacedVideo: undefined });
      if (onPosted) {
        if (!subtitles.length && story?.media?.subtitles?.length) {
          await deleteSubtitles();
          onPosted({ ...story, media: { ...story.media, burnSubtitles, subtitles: [] } });
        } else if (subtitles.length) {
          const updatedSubtitles = updateSubtitlesText(
            subtitles,
            params => handleAutosaveDraft({ ...params, draft: false })
          );
          // TODO: be sure draft subs are saved too...
          await handleAutosaveDraft({
            subtitles: updatedSubtitles,
            burnSubtitles,
            draft: false
          });
          if (burnSubtitles) await updateSubtitleIndexesInCreatomate(preview, updatedSubtitles);
        }
      }

      const mediaData = getCreatomateMediaData(preview, burnSubtitles);
      if (onMediaEdited) {
        onMediaEdited(mediaData, selectedThumbnail);
        restartData();
      } else {
        await postEdition(mediaData);
        restartData();
        preview.dispose();
      }
      if (onThirdPartyMediasReload) onThirdPartyMediasReload(true);
      onClose();
    } catch (error) {
      errorService.error("An error occurred during save edition", { error, data: preview.getSource() });
    }
  };

  return (
    <Button
      disabled={
        loading || !ready || uploadingMedia
        || pendingSubtitle || elements.length === 0
        || (montage && !storyQuestionId)
      }
      onClick={handleSaveEdition}
    >
      <Icon icon="checkmark" />
      <Trans>{btnLabel}</Trans>
    </Button>
  );
};

const SaveButton: React.FC<SaveButtonProps> = ({
  onClose, preview
}) => {
  const { data } = useContext(CreatomateContext);
  const { pendingSubtitle, uploadingMedia } = data;
  const tooltipData = () => {
    if (pendingSubtitle) return { display: true, message: "Please wait while the transcription is being processed" };
    if (data.montage && !data.storyQuestionId) return { display: true, message: "Please select a prompt in the ‘Details’ tab before saving" };
    if (uploadingMedia) return { display: true, message: "The video is being uploaded" };
    return { display: false, message: "" };
  };
  const tooltip = tooltipData();
  return (
    <>
      {tooltip.display ? (
        <Tooltip
          tip={
            <p className="Tooltip-uploaded">
              {tooltip.message}
            </p> as unknown as string
          }
        >
          <SaveBtn onClose={onClose} preview={preview} />
        </Tooltip>
      ) : (
        <SaveBtn onClose={onClose} preview={preview} />
      )}
    </>
  );
};

export default SaveButton;
